/**
 * The dashboard greeting — Phase 2.5a lever L2.
 *
 * ===========================================================================
 * WHY THIS IS A MODULE AND NOT TWO LINES INSIDE THE PAGE
 * ===========================================================================
 * "Good morning" at 11pm is the small wrongness that makes software feel
 * unattended, and it is invisible to every test that renders the page at
 * whatever time CI happens to run. Pulling the two decisions out — WHICH
 * greeting, and WHICH name — makes both of them falsifiable at every boundary
 * hour and for every shape of display name, which is what `greeting.test.ts`
 * does. A page component cannot be asked those questions.
 *
 * ===========================================================================
 * LOCAL TIME, DELIBERATELY — AND NOT THE COUNTRY'S TIMEZONE
 * ===========================================================================
 * The dashboard already carries a "Data as of …" line in the COUNTRY's zone
 * (`profile.tz`), because data freshness is a fact about the country's records.
 * The greeting is the opposite kind of statement: it is about the reader's own
 * day. A coordinator in London looking at India's pipeline at 21:00 their time
 * is having an evening, whatever the clock in Ahmedabad says, and greeting them
 * with "Good morning" because a server-side country happens to be in tomorrow
 * would be exactly the unattended feeling this lever exists to remove.
 *
 * So: greeting and date both come from the device clock; the freshness line
 * keeps the country zone and names it. Two clocks, two jobs, both labelled.
 */

/**
 * The three-way time-of-day split.
 *
 * BOUNDARIES, stated so they can be argued with rather than discovered:
 *   00:00–11:59 → morning   · 12:00–16:59 → afternoon · 17:00–23:59 → evening
 *
 * Midnight to 05:00 says "Good morning" rather than a fourth "Good night"
 * branch. Someone working at 03:00 is at the start of a working stretch, and
 * the brief names three greetings; inventing a fourth string is a copy decision
 * the owner has not made.
 */
export function timeOfDayGreeting(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * "Good morning, Shailesh" — or just "Good morning" when there is no name.
 *
 * THE EMPTY-NAME CASE IS THE POINT. `me.name` can be an empty string (a user
 * seeded without a display name) and `me` itself is null for one render after a
 * reload. Both must produce a sentence a human would write:
 *   · "Good morning, Shailesh Patel" → "Good morning, Shailesh" (first word only,
 *     which is what the design's greeting shows);
 *   · "Shailesh"        → "Good morning, Shailesh";
 *   · ""  / "   " / undefined → "Good morning" — no comma, no "there", no
 *     "undefined". A trailing ", " is the tell that a template ran with a hole
 *     in it, and "Good morning, there" is not a sentence anyone writes.
 */
export function greetingLine(now: Date, name: string | null | undefined): string {
  const greeting = timeOfDayGreeting(now);
  const first = (name ?? '').trim().split(/\s+/)[0] ?? '';
  return first === '' ? greeting : `${greeting}, ${first}`;
}

/**
 * "Wednesday, 30 July 2026" — the full date, in the reader's own locale.
 *
 * `undefined` as the locale means the browser's, so a user in India sees the
 * order they expect and a user in the US sees theirs. No format string is
 * hardcoded: `dateStyle: 'full'` is the one Intl already knows for "the whole
 * date, written out", and it is the only spelling that stays correct in a
 * locale nobody here tested.
 */
export function fullDate(now: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'full' }).format(now);
}
